import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CreditCard, ArrowLeft, Loader2, CheckCircle2, XCircle, PauseCircle,
  PlayCircle, RefreshCw, ExternalLink, AlertTriangle, Zap, Crown,
  Download, FileText, Clock, MessageSquare, PhoneCall, Plus, Star, Trash2, Shield,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_META = {
  AGENT9_STARTER_MONTHLY: { name: 'Starter', icon: Zap, color: 'text-blue-400', bg: 'bg-blue-500/10', price: '$129/mo', sms: 500, voice: 200 },
  AGENT9_STARTER_YEARLY: { name: 'Starter (Yearly)', icon: Zap, color: 'text-blue-400', bg: 'bg-blue-500/10', price: '$1,290/yr', sms: 500, voice: 200 },
  AGENT9_GROWTH_MONTHLY: { name: 'Growth', icon: Crown, color: 'text-emerald-400', bg: 'bg-emerald-500/10', price: '$179/mo', sms: 1500, voice: 500 },
  AGENT9_GROWTH_YEARLY: { name: 'Growth (Yearly)', icon: Crown, color: 'text-emerald-400', bg: 'bg-emerald-500/10', price: '$1,790/yr', sms: 1500, voice: 500 },
  AGENT9_PRO_MONTHLY: { name: 'Pro', icon: Crown, color: 'text-amber-400', bg: 'bg-amber-500/10', price: '$249/mo', sms: 5000, voice: 2000 },
  AGENT9_PRO_YEARLY: { name: 'Pro (Yearly)', icon: Crown, color: 'text-amber-400', bg: 'bg-amber-500/10', price: '$2,490/yr', sms: 5000, voice: 2000 },
};

const STATUS_STYLES = {
  ACTIVE: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  DRAFT: { label: 'Pending', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  PAUSED: { label: 'Paused', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  CANCELED: { label: 'Canceled', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  PAST_DUE: { label: 'Past Due', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
};

const CARD_BRANDS = {
  visa: { name: 'Visa', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  mastercard: { name: 'Mastercard', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  amex: { name: 'Amex', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  discover: { name: 'Discover', color: 'text-amber-400', bg: 'bg-amber-500/10' },
};

function PaymentCard({ card, isPrimary, onSetPrimary, onRemove, actionLoading }) {
  const brand = CARD_BRANDS[(card.card_brand || '').toLowerCase()] || { name: card.card_brand || 'Card', color: 'text-muted-foreground', bg: 'bg-secondary' };
  const expiry = card.card_exp_month && card.card_exp_year
    ? `${String(card.card_exp_month).padStart(2, '0')}/${String(card.card_exp_year).slice(-2)}`
    : null;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
      isPrimary ? 'border-violet-500/30 bg-violet-500/5' : 'border-border bg-muted'
    }`} data-testid={`payment-card-${card.card_last4 || 'unknown'}`}>
      <div className={`h-10 w-14 rounded-md ${brand.bg} flex items-center justify-center`}>
        <CreditCard size={20} className={brand.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{brand.name}</span>
          <span className="text-sm text-muted-foreground">ending in</span>
          <span className="text-sm font-mono font-bold text-foreground">{card.card_last4 || '****'}</span>
          {isPrimary && (
            <Badge className="text-[8px] bg-violet-500/10 text-violet-400 border-violet-500/20">
              <Star size={7} className="mr-0.5" /> Primary
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {expiry && <span className="text-xs text-muted-foreground">Expires {expiry}</span>}
          {!expiry && <span className="text-xs text-muted-foreground">Expiry not available</span>}
          {card.added_at && (
            <span className="text-xs text-muted-foreground">Added {new Date(card.added_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!isPrimary && (
          <>
            <Button size="sm" variant="ghost" onClick={onSetPrimary}
              disabled={!!actionLoading}
              className="text-[10px] text-muted-foreground hover:text-violet-400 h-7 px-2"
              data-testid={`set-primary-${card.card_last4}`}>
              {actionLoading === `primary-${card.id}` ? <Loader2 size={10} className="animate-spin" /> : <Star size={10} />}
              <span className="ml-1">Primary</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={onRemove}
              disabled={!!actionLoading}
              className="text-[10px] text-muted-foreground hover:text-red-400 h-7 px-2"
              data-testid={`remove-card-${card.card_last4}`}>
              {actionLoading === `remove-${card.id}` ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function BillingPage() {
  const { token } = useAuth();
  const { name: brandName } = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const headers = { Authorization: `Bearer ${token}` };

  const [billing, setBilling] = useState(null);
  const [events, setEvents] = useState([]);
  const [usage, setUsage] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [pastInvoices, setPastInvoices] = useState([]);
  const [trial, setTrial] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [billingYearly, setBillingYearly] = useState(false);

  const setupComplete = searchParams.get('setup') === 'complete';
  const cardUpdated = searchParams.get('card_updated') === 'true';

  useEffect(() => {
    fetchBilling();
  }, [token]);

  useEffect(() => {
    if (setupComplete) {
      // Confirm the setup with backend — activates subscription, saves card info
      const confirmSetup = async () => {
        try {
          const { data } = await axios.post(`${API}/api/billing/confirm-setup`, {}, { headers });
          if (data.confirmed) {
            setMessage(`Payment confirmed! Your ${data.plan_code?.replace('AGENT9_', '').replace('_MONTHLY', ' Monthly').replace('_YEARLY', ' Yearly') || ''} subscription is now active.${data.card_last4 ? ` Card ending in ${data.card_last4} saved.` : ''}`);
          } else {
            setMessage('Payment setup complete! Your subscription is now active.');
          }
        } catch (err) {
          setMessage('Payment completed. Refreshing billing status...');
        }
        // Refresh billing data
        fetchBilling();
        // Clean up URL params
        navigate('/settings/billing', { replace: true });
      };
      confirmSetup();
    }
    if (cardUpdated) {
      setMessage('Payment method updated successfully.');
      fetchBilling();
      navigate('/settings/billing', { replace: true });
    }
  }, [setupComplete, cardUpdated]);

  const fetchBilling = async () => {
    try {
      const [statusRes, eventsRes, usageRes, invoiceRes, pastInvRes, trialRes, pmRes] = await Promise.all([
        axios.get(`${API}/api/billing/status`, { headers }),
        axios.get(`${API}/api/billing/events?limit=10`, { headers }).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/api/billing/usage`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/billing/invoice`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/billing/invoices?limit=12`, { headers }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API}/api/billing/trial`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/billing/payment-methods`, { headers }).catch(() => ({ data: { payment_methods: [] } })),
      ]);
      setBilling(statusRes.data);
      setEvents(eventsRes.data?.events || []);
      setUsage(usageRes.data);
      setInvoice(invoiceRes.data);
      setPastInvoices(pastInvRes.data?.invoices || []);
      setTrial(trialRes.data);
      setPaymentMethods(pmRes.data?.payment_methods || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleSubscribe = async (planCode) => {
    setActionLoading('subscribe');
    try {
      const { data } = await axios.post(`${API}/api/billing/subscribe`, { plan_code: planCode }, { headers });
      if (data.hosted_payment_url) {
        window.location.href = data.hosted_payment_url;
      } else {
        setMessage('Subscription created. Redirecting to payment...');
        fetchBilling();
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to create subscription');
    } finally { setActionLoading(''); }
  };

  const handleUpgrade = async (planCode) => {
    setActionLoading('upgrade');
    try {
      const { data } = await axios.post(`${API}/api/billing/upgrade`, { plan_code: planCode }, { headers });
      if (data.hosted_payment_url) {
        window.location.href = data.hosted_payment_url;
      } else {
        fetchBilling();
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to upgrade');
    } finally { setActionLoading(''); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    setActionLoading('cancel');
    try {
      await axios.post(`${API}/api/billing/cancel`, { reason: 'User requested' }, { headers });
      setMessage('Subscription canceled.');
      fetchBilling();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Cancel failed');
    } finally { setActionLoading(''); }
  };

  const handlePause = async () => {
    setActionLoading('pause');
    try {
      await axios.post(`${API}/api/billing/pause`, {}, { headers });
      setMessage('Subscription paused.');
      fetchBilling();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Pause failed');
    } finally { setActionLoading(''); }
  };

  const handleResume = async () => {
    setActionLoading('resume');
    try {
      await axios.post(`${API}/api/billing/resume`, {}, { headers });
      setMessage('Subscription resumed!');
      fetchBilling();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Resume failed');
    } finally { setActionLoading(''); }
  };

  const handleUpdatePayment = async () => {
    setActionLoading('payment');
    try {
      const { data } = await axios.post(`${API}/api/billing/update-payment`, {}, { headers });
      if (data.hosted_payment_url) {
        window.location.href = data.hosted_payment_url;
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to update payment method');
    } finally { setActionLoading(''); }
  };

  const handleAddCard = async () => {
    setActionLoading('add-card');
    try {
      const { data } = await axios.post(`${API}/api/billing/payment-methods`, {}, { headers });
      if (data.hosted_payment_url) {
        window.location.href = data.hosted_payment_url;
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to add payment method');
    } finally { setActionLoading(''); }
  };

  const handleSetPrimary = async (methodId) => {
    setActionLoading(`primary-${methodId}`);
    try {
      await axios.put(`${API}/api/billing/payment-methods/${methodId}/primary`, {}, { headers });
      setMessage('Primary card updated.');
      fetchBilling();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to set primary card');
    } finally { setActionLoading(''); }
  };

  const handleRemoveCard = async (methodId) => {
    if (!window.confirm('Remove this payment method?')) return;
    setActionLoading(`remove-${methodId}`);
    try {
      await axios.delete(`${API}/api/billing/payment-methods/${methodId}`, { headers });
      setMessage('Payment method removed.');
      fetchBilling();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to remove card');
    } finally { setActionLoading(''); }
  };

  const handleDownloadPdf = async (period) => {
    setPdfLoading(true);
    try {
      const url = period
        ? `${API}/api/billing/invoice/pdf?period=${period}`
        : `${API}/api/billing/invoice/pdf`;
      const { data } = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${brandName}-Invoice-${period || 'current'}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setMessage('Failed to download invoice PDF');
    } finally { setPdfLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
    </div>
  );

  const plan = billing?.plan_code ? PLAN_META[billing.plan_code] : null;
  const sts = billing?.status ? STATUS_STYLES[billing.status] : null;
  const hasSub = billing?.has_subscription;
  const PlanIcon = plan?.icon || Zap;

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-4 sm:p-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-foreground" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-foreground">Billing & Subscription</h1>
          <p className="text-xs text-muted-foreground">Manage your {brandName} plan</p>
        </div>
      </div>

      {message && (
        <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${message.includes('fail') || message.includes('error') ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'}`}
          data-testid="billing-message">
          <CheckCircle2 size={14} /> {message}
          <button onClick={() => setMessage('')} className="ml-auto text-xs opacity-60 hover:opacity-100">dismiss</button>
        </div>
      )}

      {/* Trial Status */}
      {trial?.has_trial && trial.trial_status !== 'converted' && (
        <Card className={`border-border bg-card ${trial.trial_status === 'active' ? 'ring-1 ring-emerald-500/20' : 'ring-1 ring-amber-500/20'}`} data-testid="trial-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Clock size={16} className={trial.trial_status === 'active' ? 'text-emerald-400' : 'text-amber-400'} />
                {trial.trial_status === 'active' ? 'Free Trial Active' : 'Free Trial Ended'}
              </CardTitle>
              <Badge className={`text-xs ${trial.trial_status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}
                data-testid="trial-status-badge">
                {trial.trial_status === 'active' ? 'Active' : trial.trial_status === 'exhausted' ? 'Resources Used' : 'Expired'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {trial.trial_status === 'active' && trial.trial_ends_at && (
              <div className="flex items-center gap-2 p-2.5 bg-emerald-500/5 border border-emerald-500/10 rounded-lg">
                <Clock size={13} className="text-emerald-400 shrink-0" />
                <span className="text-xs text-emerald-300">
                  Trial expires: <strong>{new Date(trial.trial_ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</strong>
                </span>
              </div>
            )}

            {(trial.trial_status === 'expired' || trial.trial_status === 'exhausted') && (
              <div className="flex items-center gap-2 p-3 bg-amber-500/5 border border-amber-500/10 rounded-lg" data-testid="trial-upgrade-prompt">
                <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-amber-300 font-medium">
                    {trial.trial_status === 'exhausted' ? 'Trial resources exhausted' : 'Your trial has expired'}
                  </p>
                  <p className="text-xs text-amber-400/70 mt-0.5">Subscribe to a plan below to continue using SMS and AI voice features.</p>
                </div>
              </div>
            )}

            {/* Trial SMS Meter */}
            <div data-testid="trial-sms-meter">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-foreground font-medium flex items-center gap-1.5">
                  <MessageSquare size={13} className="text-muted-foreground" /> Trial SMS
                </span>
                <span className="text-xs text-muted-foreground">{trial.trial_sms_used} / {trial.trial_sms_limit}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${trial.trial_sms_used >= trial.trial_sms_limit ? 'bg-red-500' : trial.trial_sms_used >= trial.trial_sms_limit * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, trial.trial_sms_limit > 0 ? (trial.trial_sms_used / trial.trial_sms_limit) * 100 : 0)}%` }} />
              </div>
              <span className="text-[10px] text-muted-foreground mt-1 block">{trial.trial_sms_remaining} remaining</span>
            </div>

            {/* Trial Voice Meter */}
            <div data-testid="trial-voice-meter">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-foreground font-medium flex items-center gap-1.5">
                  <PhoneCall size={13} className="text-muted-foreground" /> Trial AI Voice
                </span>
                <span className="text-xs text-muted-foreground">{trial.trial_voice_minutes_used} / {trial.trial_voice_minutes_limit} min</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${trial.trial_voice_minutes_used >= trial.trial_voice_minutes_limit ? 'bg-red-500' : trial.trial_voice_minutes_used >= trial.trial_voice_minutes_limit * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, trial.trial_voice_minutes_limit > 0 ? (trial.trial_voice_minutes_used / trial.trial_voice_minutes_limit) * 100 : 0)}%` }} />
              </div>
              <span className="text-[10px] text-muted-foreground mt-1 block">{trial.trial_voice_minutes_remaining} min remaining</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan */}
      {hasSub ? (
        <Card className="border-border bg-card" data-testid="current-plan-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <CreditCard size={16} className="text-amber-400" /> Current Plan
              </CardTitle>
              {sts && <Badge className={`text-xs ${sts.color}`} data-testid="billing-status">{sts.label}</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
              <div className={`h-12 w-12 rounded-xl ${plan?.bg || 'bg-muted'} flex items-center justify-center`}>
                <PlanIcon size={24} className={plan?.color || 'text-muted-foreground'} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground">{plan?.name || billing?.plan_name || 'Unknown'}</h3>
                <p className="text-sm text-muted-foreground">{plan?.price || `$${(billing?.amount_cents || 0) / 100}/mo`}</p>
              </div>
              {billing?.status === 'ACTIVE' && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Next bill</p>
                  <p className="text-sm text-foreground font-medium" data-testid="next-bill-date">
                    {billing.next_bill_at ? new Date(billing.next_bill_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Pending'}
                  </p>
                </div>
              )}
            </div>

            {/* Card info - now in Payment Methods section below */}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              {billing?.status === 'ACTIVE' && (
                <>
                  <Button size="sm" variant="outline" onClick={handlePause} disabled={!!actionLoading}
                    className="border-border text-foreground hover:text-foreground gap-1.5"
                    data-testid="pause-btn">
                    {actionLoading === 'pause' ? <Loader2 size={12} className="animate-spin" /> : <PauseCircle size={12} />}
                    Pause
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={!!actionLoading}
                    className="border-red-500/30 text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-1.5"
                    data-testid="cancel-btn">
                    {actionLoading === 'cancel' ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                    Cancel
                  </Button>
                </>
              )}
              {billing?.status === 'PAUSED' && (
                <>
                  <Button size="sm" onClick={handleResume} disabled={!!actionLoading}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5"
                    data-testid="resume-btn">
                    {actionLoading === 'resume' ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                    Resume
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={!!actionLoading}
                    className="border-red-500/30 text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-1.5"
                    data-testid="cancel-btn-paused">
                    <XCircle size={12} /> Cancel
                  </Button>
                </>
              )}
              {billing?.status === 'CANCELED' && (
                <p className="text-xs text-muted-foreground">Subscription was canceled on {billing.canceled_at ? new Date(billing.canceled_at).toLocaleDateString() : 'N/A'}. Choose a new plan below to resubscribe.</p>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Payment Methods */}
      {hasSub && (
        <Card className="border-border bg-card" data-testid="payment-methods-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Shield size={16} className="text-violet-400" /> Payment Methods
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={handleAddCard} disabled={!!actionLoading}
                className="text-xs text-violet-400 hover:text-violet-300 gap-1"
                data-testid="add-card-btn">
                {actionLoading === 'add-card' ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                Add Card
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {paymentMethods.length === 0 && !billing?.card_last4 && (
              <div className="text-center py-6">
                <CreditCard size={28} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No payment methods saved</p>
                <p className="text-xs text-muted-foreground mt-1">Add a card to activate recurring billing</p>
              </div>
            )}
            {/* Show from billing record if no payment_methods yet */}
            {paymentMethods.length === 0 && billing?.card_last4 && (
              <PaymentCard
                card={{ card_last4: billing.card_last4, card_brand: billing.card_brand, card_exp_month: billing.card_exp_month, card_exp_year: billing.card_exp_year, is_primary: true }}
                isPrimary
                onSetPrimary={() => {}}
                onRemove={() => {}}
                actionLoading=""
              />
            )}
            {paymentMethods.map(pm => (
              <PaymentCard key={pm.id}
                card={pm}
                isPrimary={pm.is_primary}
                onSetPrimary={() => handleSetPrimary(pm.id)}
                onRemove={() => handleRemoveCard(pm.id)}
                actionLoading={actionLoading}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Usage Meters — only show when subscribed */}
      {usage && hasSub && billing?.status === 'ACTIVE' && (
        <Card className="border-border bg-card" data-testid="usage-meters-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base">Usage This Month</CardTitle>
              <Badge className="text-[10px] bg-muted text-muted-foreground border-border">{usage.period}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* SMS Meter */}
            <div data-testid="sms-usage-meter">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-foreground font-medium">SMS Messages</span>
                <span className="text-xs text-muted-foreground">{usage.sms.used} / {usage.sms.limit}</span>
              </div>
              <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${usage.sms.pct >= 90 ? 'bg-red-500' : usage.sms.pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, usage.sms.pct)}%` }} />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-muted-foreground">{usage.sms.remaining} remaining</span>
                {usage.sms.overage > 0 && (
                  <span className="text-[10px] text-amber-400" data-testid="sms-overage">
                    {usage.sms.overage} overage (${(usage.sms.overage_cost_cents / 100).toFixed(2)})
                  </span>
                )}
              </div>
            </div>

            {/* Voice Minutes Meter */}
            <div data-testid="voice-usage-meter">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-foreground font-medium">AI Voice Minutes</span>
                <span className="text-xs text-muted-foreground">{usage.voice.used} / {usage.voice.limit}</span>
              </div>
              <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${usage.voice.pct >= 90 ? 'bg-red-500' : usage.voice.pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, usage.voice.pct)}%` }} />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-muted-foreground">{usage.voice.remaining} min remaining</span>
                {usage.voice.overage > 0 && (
                  <span className="text-[10px] text-amber-400" data-testid="voice-overage">
                    {usage.voice.overage} min overage (${(usage.voice.overage_cost_cents / 100).toFixed(2)})
                  </span>
                )}
              </div>
            </div>

            {/* Total overage */}
            {usage.total_overage_cents > 0 && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/10" data-testid="total-overage">
                <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                <span className="text-xs text-amber-300">
                  Total overage this period: <strong>${(usage.total_overage_cents / 100).toFixed(2)}</strong> — 
                  Consider upgrading for higher limits.
                </span>
              </div>
            )}

            {/* Overage rates */}
            <p className="text-[10px] text-muted-foreground">
              Overage rates: SMS $0.03/msg, AI Voice $0.035/min
            </p>
          </CardContent>
        </Card>
      )}

      {/* Current Invoice / Billing Estimate — only show when subscribed */}
      {invoice && hasSub && billing?.status === 'ACTIVE' && (
        <Card className="border-border bg-card" data-testid="invoice-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <FileText size={15} className="text-muted-foreground" />
                Billing Estimate — {invoice.period}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge className={`text-xs ${invoice.total_overage_cents > 0 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-muted text-muted-foreground border-border'}`}>
                  ${(invoice.total_cents / 100).toFixed(2)}
                </Badge>
                <Button size="sm" variant="ghost" onClick={() => handleDownloadPdf(invoice.period)}
                  disabled={pdfLoading}
                  className="text-xs text-muted-foreground hover:text-foreground gap-1 h-7 px-2"
                  data-testid="download-invoice-pdf-btn">
                  {pdfLoading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invoice.line_items?.map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-2">
                    {item.type === 'plan' ? (
                      <CreditCard size={13} className="text-muted-foreground" />
                    ) : (
                      <AlertTriangle size={13} className="text-amber-400" />
                    )}
                    <span className="text-sm text-foreground">{item.description}</span>
                  </div>
                  <span className={`text-sm font-medium ${item.type === 'plan' ? 'text-foreground' : 'text-amber-400'}`}>
                    ${(item.amount_cents / 100).toFixed(2)}
                  </span>
                </div>
              ))}
              {/* Total */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-sm font-semibold text-foreground">Total</span>
                <span className="text-base font-bold text-foreground" data-testid="invoice-total">
                  ${(invoice.total_cents / 100).toFixed(2)}
                </span>
              </div>
            </div>
            {invoice.total_overage_cents > 0 && (
              <p className="text-[10px] text-amber-400/70 mt-3">
                Overage charges will be billed at the end of your billing cycle.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Plan Selection / Upgrade */}
      <Card className="border-border bg-card" data-testid="plan-selection-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-foreground text-base">
                {hasSub && billing?.status === 'ACTIVE' ? 'Change Plan' : 'Choose a Plan'}
              </CardTitle>
              <CardDescription className="text-muted-foreground text-xs mt-1">
                {hasSub && billing?.status === 'ACTIVE' ? 'Upgrade or downgrade your subscription' : 'Select a plan to get started'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-full p-0.5 border border-border" data-testid="billing-yearly-toggle">
              <button onClick={() => setBillingYearly(false)} className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${!billingYearly ? 'bg-emerald-500 text-white' : 'text-muted-foreground'}`}>Monthly</button>
              <button onClick={() => setBillingYearly(true)} className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${billingYearly ? 'bg-emerald-500 text-white' : 'text-muted-foreground'}`}>Yearly</button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { base: 'STARTER', name: 'Starter', monthly: 129, icon: Zap, color: 'text-blue-400', bg: 'bg-blue-500/10', sms: 500, voice: 200, desc: 'AI answers calls & books appointments' },
              { base: 'GROWTH', name: 'Growth', monthly: 179, icon: Crown, color: 'text-emerald-400', bg: 'bg-emerald-500/10', sms: 1500, voice: 500, popular: true, desc: 'More automation, more leads captured' },
              { base: 'PRO', name: 'Pro', monthly: 249, icon: Crown, color: 'text-amber-400', bg: 'bg-amber-500/10', sms: 5000, voice: 2000, desc: 'Maximum AI power for top producers' },
            ].map(p => {
              const code = `AGENT9_${p.base}_${billingYearly ? 'YEARLY' : 'MONTHLY'}`;
              const price = billingYearly ? Math.round(p.monthly * 10 / 12) : p.monthly;
              const isCurrent = billing?.plan_code === code && billing?.status === 'ACTIVE';
              const Icon = p.icon;
              return (
                <div key={code} className={`relative rounded-xl border p-4 transition-colors ${isCurrent ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border bg-muted/20 hover:border-border'}`}
                  data-testid={`plan-option-${code}`}>
                  {p.popular && <span className="absolute -top-2.5 left-3 text-[9px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-medium">Most Popular</span>}
                  <div className={`h-9 w-9 rounded-lg ${p.bg} flex items-center justify-center mb-3`}>
                    <Icon size={18} className={p.color} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">{p.name}</h3>
                  <p className="text-lg font-bold text-foreground mt-1">${price}/mo</p>
                  {billingYearly && <p className="text-[10px] text-emerald-400">${p.monthly * 10}/yr — save ${p.monthly * 2}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{p.desc}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{p.sms} SMS + {p.voice} voice min</p>
                  {isCurrent ? (
                    <Badge className="mt-3 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">Current Plan</Badge>
                  ) : (
                    <Button size="sm" onClick={() => hasSub && billing?.status === 'ACTIVE' ? handleUpgrade(code) : handleSubscribe(code)}
                      disabled={!!actionLoading}
                      className={`mt-3 w-full text-xs ${p.popular ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-accent hover:bg-muted-foreground text-foreground'}`}
                      data-testid={`select-plan-${code}`}>
                      {actionLoading === 'subscribe' || actionLoading === 'upgrade' ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                      {hasSub && billing?.status === 'ACTIVE' ? 'Switch' : 'Subscribe'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Past Invoices */}
      {pastInvoices.length > 0 && (
        <Card className="border-border bg-card" data-testid="past-invoices-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base flex items-center gap-2">
              <FileText size={15} className="text-muted-foreground" /> Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pastInvoices.map((inv, i) => (
                <div key={inv.period || i} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                      <FileText size={14} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-foreground font-medium">{inv.period}</p>
                      <p className="text-[10px] text-muted-foreground">{inv.plan_code?.replace('AGENT9_', '').replace('_MONTHLY', '') || 'Plan'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {inv.total_overage_cents > 0 && (
                      <span className="text-[10px] text-amber-400">+${(inv.total_overage_cents / 100).toFixed(2)} overage</span>
                    )}
                    <span className="text-sm font-medium text-foreground">${(inv.total_cents / 100).toFixed(2)}</span>
                    <Button size="sm" variant="ghost" onClick={() => handleDownloadPdf(inv.period)}
                      disabled={pdfLoading}
                      className="text-xs text-muted-foreground hover:text-foreground gap-1 h-7 px-2"
                      data-testid={`download-pdf-${inv.period}`}>
                      <Download size={12} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Events */}
      {events.length > 0 && (
        <Card className="border-border bg-card" data-testid="billing-events-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base">Billing History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {events.map((evt, i) => (
                <div key={evt.event_id || i} className="flex items-center justify-between p-2.5 bg-muted rounded-lg text-xs">
                  <div className="flex items-center gap-2">
                    {evt.event_type?.includes('success') ? <CheckCircle2 size={12} className="text-emerald-400" /> :
                     evt.event_type?.includes('failed') ? <XCircle size={12} className="text-red-400" /> :
                     evt.event_type?.includes('canceled') ? <XCircle size={12} className="text-muted-foreground" /> :
                     <CreditCard size={12} className="text-blue-400" />}
                    <span className="text-foreground font-medium">{evt.event_type?.replace('subscription.', '').replace(/\./g, ' ')}</span>
                  </div>
                  <span className="text-muted-foreground">{evt.received_at ? new Date(evt.received_at).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
